// Utilities & Hooks
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { useAuth } from "../../providers/auth-context";
import { handleAuthenticationTokensRemovalFromStorage, tokenRefresher } from "../tokenRefresher";
import { LocalStorageActions } from "../../utilities/handleLocalStorage";
import handleCheckVisitedPageAccessType from "../../utilities/handleCheckVisitedPageAccessType";
import fetchHandler from "../fetchHandler";

// Interfaces
import {
  AuthenticationLoginPayload,
  AuthenticationLoginResponse,
  AuthenticationResetPasswordPayloadFields,
} from "./interfaces";

/**
 *
 * Handle User login into the application and
 * saving authentication tokens in local storage
 *
 */
export function useAuthenticationLogin() {
  const { handleAuthenticationTokens } = useAuth();

  return useMutation({
    mutationFn: async (credentials: AuthenticationLoginPayload) => {
      return (await fetchHandler("POST", "auth/login", credentials)) as AuthenticationLoginResponse;
    },
    onSuccess: response => {
      // Exit function and show info modal that the user is not active
      if (response.is_enabled === false) {
        return { is_enabled: false };
      }

      // Exit function and update the login UI in case 2FA is enabled
      if (response.is_2fa_enabled === true) {
        return { is_2fa_enabled: true };
      }

      // Throw error if there are no tokens available
      if (response.long_lived && !response.access_token) {
        throw new Error("No Access Token received from the API.");
      }

      if (!response.long_lived && (!response.access_token || !response.refresh_token)) {
        throw new Error("No token(s) received from the API.");
      }

      // Save authentication tokens in local storage and update authentication context
      handleAuthenticationTokens({
        access_token: response.access_token,
        refresh_token: !response.long_lived ? response.refresh_token : null,
        expires_in: response.expires_in,
        long_lived: response.long_lived,
        autologin: false,
      });
    },
    onError: error => error,
  });
}

/**
 *
 * Send autologin request for the selected user
 *
 * @param email The email of the user that we want to login as
 * @param token The token used for allowing autologin functionality
 *
 */
export function useAuthenticationAutologin(
  email: string,
  token: string,
  existingAccessToken: string | null,
) {
  return useQuery({
    queryKey: ["auth-autologin", email, token],
    queryFn: async () => {
      return await fetchHandler(
        "GET",
        `autologin?email=${encodeURIComponent(email)}&token=${token}`,
      );
    },
    // Only send the API request if there are valid email and token values,
    // and there's no existing access token saved in local storage for the user
    enabled: !existingAccessToken && !!email && !!token,
    meta: {
      errorMessage: "Autologin Failed",
    },
    retry: 0,
    gcTime: 0,
  });
}

/**
 *
 * Sends an email to the provided email for resetting the password
 *
 */
export function useAuthenticationForgotPassword() {
  return useMutation({
    mutationFn: async (email: string) => {
      return fetchHandler("POST", "auth/forgot-password", { email });
    },
    onSuccess: () => {
      toast.success("If an account exists you will receive an email with a password reset link.");
    },
    onError: error => error,
  });
}

/**
 *
 * Resets the user's password based on the
 * received email's "token" value that is used as
 * identifier to which user should the password be updated
 *
 */
export function useAuthenticationResetPassword() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (resetPasswordDetails: AuthenticationResetPasswordPayloadFields) => {
      return await fetchHandler("POST", "auth/reset-password", resetPasswordDetails);
    },
    onSuccess: () => {
      toast.success("Password updated! You will be redirected to login page shortly...");

      setTimeout(() => {
        navigate("/login/");
      }, 3000);
    },
    onError: error => error,
  });
}

/**
 *
 * Logout the user from the application
 *
 */
export function useAuthenticationLogout() {
  const { handleAuthenticationTokens, handleClearAuthenticationContext } = useAuth();

  // Check if "access" token exists in local storage
  const accessToken = LocalStorageActions.getItem("accessToken");

  return useMutation({
    mutationFn: async () => {
      // Prevent API request if there's no access token in storage
      if (!accessToken) return;

      return fetchHandler("POST", "auth/logout");
    },
    onSuccess: () => {
      // Update the context to signal that the user has logged out
      handleAuthenticationTokens({
        access_token: "",
        refresh_token: "",
        expires_in: 0,
        autologin: false,
        long_lived: false,
      });

      // Remove the saved properties from local storage
      handleAuthenticationTokensRemovalFromStorage();

      // Clear the context state
      handleClearAuthenticationContext();
    },
    onError: error => error,
  });
}

/**
 *
 * Regenerate the current access token of the user.
 *
 * If the user is currently viewing some of the pages that are
 * marked as "public" or "auth" then no attempts to regenerate
 * an access token will be made.
 *
 * If the user is currently viewing some of the pages that are
 * marked as "protected" then every 10 seconds the status of the
 * access token will be checked and if necessary a regenerate request will be sent.
 *
 * @param isAuthenticated Is the user currently logged in into the application
 *
 */
export function useAuthenticationRefreshToken(isAuthenticated: boolean) {
  const [isTokenRefreshEnabled, setIsTokenRefreshEnabled] = useState<boolean>(false);
  useEffect(() => {
    const typeOfPage: "public" | "protected" = handleCheckVisitedPageAccessType();
    setIsTokenRefreshEnabled(typeOfPage === "protected");
  }, [isAuthenticated]);

  return useQuery({
    queryKey: ["access-token"],
    queryFn: () => {
      // Do not send a request if the user is not logged in
      if (!isAuthenticated) return { access_token: "", refresh_token: "" };

      // Call the refresher function to get new tokens
      return tokenRefresher();
    },

    // Trigger token refresher function every 10 seconds
    refetchInterval: 1000 * 10,
    retry: 0,
    enabled: isTokenRefreshEnabled,
  });
}

/**
 *
 * Revokes all issued access tokens and logs out the user from all devices
 *
 */
export function useAuthenticationLogoutFromAllDevices() {
  const { handleAuthenticationTokens, handleClearAuthenticationContext } = useAuth();

  return useMutation({
    mutationFn: async () => {
      return await fetchHandler("POST", "user/revoke-access-token");
    },
    onSuccess: () => {
      // Update the context to signal that the user has logged out
      handleAuthenticationTokens({
        access_token: "",
        refresh_token: "",
        expires_in: 0,
        autologin: false,
        long_lived: false,
      });

      // Remove the saved properties from local storage
      handleAuthenticationTokensRemovalFromStorage();

      // Clear the context state
      handleClearAuthenticationContext();
    },
    onError: error => error,
  });
}
